import Image from "next/image";
import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
const AnimatedSection = dynamic(() => import('../../components/Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface RhinoplastyHorizontalData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  heading2?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",
  "subheading.text",
  "subheading.title",
  "heading2.text",
  "heading2.title",

  // text
  "text.text",

  // button
  "button.label",
  "button.href",

  // images
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",

  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface RhinoplastyHorizontalProps {
  data?: RhinoplastyHorizontalData;
  zIndex: number;
  thresholds: number[][];
  mobileThresholds: number[][];
  transitionTime?: number;
  helperRotation?: number;
}

export default function RhinoplastyHorizontal({ data, zIndex, thresholds, mobileThresholds, transitionTime, helperRotation }: RhinoplastyHorizontalProps) {
  const mainImage = data?.images?.[0]?.images_id || null;
  const mainImageId = mainImage?.image_file?.image?.id || "";
  const mainImageFilename = mainImage?.image_file?.image?.filename_download || "";
  const mainImageAlt = mainImage?.alt || "";

  const secondaryImage = data?.images?.[1]?.images_id || null;
  const secondaryImageId = secondaryImage?.image_file?.image?.id || "";
  const secondaryImageFilename = secondaryImage?.image_file?.image?.filename_download || "";
  const secondaryImageAlt = secondaryImage?.alt || "";

  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32 bg-white top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { x: 0, opacity: 1 },
              above: { x: "100vw", opacity: 1 },
              below: { x: "-100vw", opacity: 1 },
            },
            mobile: {
              visible: { x: 0, opacity: 1 },
              above: { x: "100vw", opacity: 1 },
              below: { x: 0, opacity: 0 },
            }
          }}
        >
          <div className="flex flex-col lg:flex-row h-full w-full lg:w-[100vw] overflow-hidden">
            <AnimatedSection
              section="div"
              thresholds={thresholds[1]}
              className="flex flex-col lg:flex-row h-full"
              transitionTime={transitionTime}
              variants={{
                desktop: {
                  visible: { x: "-40vw", opacity: 1 },
                  above: { x: 0, opacity: 1 },
                  below: { x: "-40vw", opacity: 0 },
                }
              }}
            >
              <div className="w-full lg:w-[40vw] h-auto lg:h-full hidden lg:flex flex-col items-center lg:items-start justify-start pt-8 lg:pt-20 px-6 lg:pl-[10vw]">
                <div className="gl-desktop-scale w-full">
                  <h2 className="text-primary text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-center lg:text-left">
                    {data?.heading?.title}
                  </h2>
                  <div className="text-primary text-xl lg:text-3xl font-normal font-greycliff leading-8 lg:leading-10 tracking-tight mt-2 text-center lg:text-left">
                    {data?.subheading?.title}
                  </div>
                </div>
              </div>

              <AnimatedSection 
                section="div"
                thresholds={thresholds[1]} 
                className="w-full lg:w-[60vw] h-48 lg:h-full flex items-center justify-center shrink-0 mt-0 relative z-20" 
                transitionTime={transitionTime}
                variants={{
                  desktop: {
                    visible: { width: "40vw", opacity: 1 },
                    above: { width: "60vw", opacity: 1 },
                    below: { width: "40vw", opacity: 1 },
                  }
                }}
              >
                <AnimatedSection 
                  section="div"
                  mobileThresholds={mobileThresholds[0]}
                  className="relative w-full h-full z-10"
                  variants={{
                    desktop: {
                      visible: { scale: 1, y: 0, x: 0, opacity: 1 },
                      above: { scale: 1, y: 0, x: 0, opacity: 1 },
                      below: { scale: 1, y: 0, x: 0, opacity: 1 },
                    },
                    mobile: {
                      visible: { scale: 1, y: 0, x: 0, opacity: 1, zIndex: 20 },
                      above: { scale: 4.2, y: 290, x: "-22%", opacity: 1, zIndex: 20 },
                      below: { scale: 1, y: 0, x: 0, opacity: 1, zIndex: 20 },
                    }
                  }}
                  withZIndex={true}
                >
                  <Image 
                    src={mainImageId ? getReadableImageUrl(mainImageId, mainImageFilename) : ""}
                    alt={mainImageAlt}
                    fill
                    className="object-cover object-center lg:object-top" 
                    quality={100}
                    priority
                  />
                </AnimatedSection>
              </AnimatedSection>

              {/* Third column - 100% width */}
              <AnimatedSection
                section="div"
                mobileThresholds={mobileThresholds[0]}
                className="w-full lg:w-[35vw] h-auto lg:h-full flex items-center justify-center shrink-0 px-6 lg:px-0 mt-4 lg:mt-0 relative z-10"
                transitionTime={transitionTime}
                variants={{
                  desktop: {
                    visible: { x: 0, opacity: 1 },
                    above: { x: 0, opacity: 1 },
                    below: { x: 0, opacity: 1 },
                  },
                  mobile: {
                    visible: { x: 0, opacity: 1 },
                    above: { x: 0, opacity: 0 },
                    below: { x: 0, opacity: 1 },
                  }
                }}
              >
                <div className="w-full h-auto text-sm lg:text-lg lg:w-96 flex flex-col justify-start items-start space-y-4 xl:gap-12 gl-desktop-scale xl:text-center">
                  <h2 
                    className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-center lg:text-left"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || "")}
                  />
                  
                  <div className="w-full lg:pr-3 lg:pb-6 flex justify-start items-center overflow-hidden">
                    <div className="w-full lg:w-96 flex flex-col justify-start items-start space-y-4 lg:space-y-6" dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")} />
                  </div>

                  <div className="w-full flex justify-center lg:justify-start">
                    <a 
                      href={data?.button?.href || "#"} 
                      className="w-full lg:w-auto h-14 lg:h-16 px-8 lg:px-9 py-3 bg-primary rounded-full flex justify-center items-center gap-5"
                    >
                      <span className="text-white lg:text-xl font-normal font-greycliff leading-7 tracking-tight">
                        {data?.button?.label}
                      </span>
                    </a>
                  </div>
                </div>
              </AnimatedSection>

              <div className="w-full lg:w-[25vw] h-auto lg:h-full hidden lg:flex items-center lg:items-start justify-center shrink-0 mt-8 lg:mt-0 pb-8 lg:pb-0">
                <div className="w-full max-w-xs lg:w-80 h-auto lg:h-96 flex-col justify-start items-start gap-6 lg:gap-8 inline-flex gl-desktop-scale">
                  <div className="w-full h-48 lg:h-60 justify-center items-center inline-flex">
                    <div className="relative w-full lg:w-80 h-full">
                      <Image 
                        src={secondaryImageId ? getReadableImageUrl(secondaryImageId, secondaryImageFilename) : ""}
                        alt={secondaryImageAlt}
                        fill
                        className="object-cover" 
                        priority
                        quality={90}
                      />
                    </div>
                  </div>
                  <div className="self-stretch text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-center lg:text-left">
                    {data?.heading2?.title}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full bg-white py-8 lg:py-16">
          <div className="flex flex-col xl:flex-row h-full w-full overflow-hidden">
            <div className="flex flex-col xl:flex-row w-full h-full">
              {/* <div className="w-full xl:w-[40vw] h-auto xl:h-full hidden xl:flex flex-col items-center xl:items-start justify-start pt-8 xl:pt-20 px-6 xl:pl-[10vw]">
                <div className="gl-desktop-scale w-full">
                  <h2 className="text-primary text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-center xl:text-left">
                    {data?.heading?.title}
                  </h2>
                  <div className="text-primary text-xl xl:text-3xl font-normal font-greycliff leading-8 xl:leading-10 tracking-tight mt-2 text-center xl:text-left">
                    {data?.subheading?.title}
                  </div>
                </div>
              </div> */}

              <div className="w-full xl:w-[35%] h-screen flex items-center justify-center shrink-0 mt-0 relative z-20">
                <div className="relative w-full h-full z-10">
                  <Image 
                    src={mainImageId ? getReadableImageUrl(mainImageId, mainImageFilename) : ""}
                    alt={mainImageAlt}
                    fill
                    className="object-cover object-center xl:object-top" 
                    quality={100}
                    priority
                  />
                </div>
              </div>

              {/* Third column - 100% width */}
              <div className="w-full xl:w-[35%] h-full flex items-center justify-center shrink-0 px-6 xl:px-0 mt-4 xl:mt-0 relative z-10">
                <div className="w-full h-auto text-sm xl:text-lg xl:w-96 flex flex-col justify-start items-start space-y-4 xl:gap-12 gl-desktop-scale xl:text-center">
                  <h2 
                    className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-center xl:text-left"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || "")}
                  />
                  
                  <div className="w-full xl:pr-3 xl:pb-6 flex justify-start items-center overflow-hidden">
                    <div className="w-full xl:w-96 flex flex-col justify-start items-start space-y-4 xl:space-y-6" dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")} />
                  </div>

                  <div className="w-full flex justify-center xl:justify-start">
                    <a 
                      href={data?.button?.href || "#"} 
                      className="w-full xl:w-auto h-14 xl:h-16 px-8 xl:px-9 py-3 bg-primary rounded-full flex justify-center items-center gap-5"
                    >
                      <span className="text-white xl:text-xl font-normal font-greycliff leading-7 tracking-tight">
                        {data?.button?.label}
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="w-full xl:w-[30%] h-full hidden xl:flex items-center xl:items-start justify-center shrink-0 mt-8 xl:mt-0 pb-8 xl:pb-0">
                <div className="w-full max-w-xs xl:w-80 h-auto xl:h-96 flex-col justify-start items-start gap-6 xl:gap-8 inline-flex gl-desktop-scale">
                  <div className="w-full h-48 xl:h-60 justify-center items-center inline-flex">
                    <div className="relative w-full xl:w-80 h-full">
                      <Image 
                        src={secondaryImageId ? getReadableImageUrl(secondaryImageId, secondaryImageFilename) : ""}
                        alt={secondaryImageAlt}
                        fill
                        className="object-cover" 
                        priority
                        quality={90}
                      />
                    </div>
                  </div>
                  <div className="self-stretch text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-center xl:text-left">
                    {data?.heading2?.title}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
